/**
 * CareLogsPage
 *
 * Main Care Logs page with timeline view, filtering, and entry creation.
 * Follows the layout pattern from CaseFilePage (sidebar + main content).
 */

import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useCaseFile } from '../contexts/CaseFileContext';
import { Sidebar, Header } from '../components/dashboard';
import {
  CareLogForm,
  CareLogsList,
  CareLogDetail,
  DateRangeFilter,
  TagFilter,
  CareLogTag,
} from '../components/carelogs';
import { Plus, ArrowLeft, X, Filter, AlertTriangle } from 'lucide-react';

type CaseFilePermission = 'owner' | 'editor' | 'logger' | 'viewer';

/** Returns true if the permission level allows write actions (create/edit logs) */
function hasWriteAccess(permission: CaseFilePermission): boolean {
  // logger permission allows creating care logs
  return permission === 'owner' || permission === 'editor' || permission === 'logger';
}

interface GetCaseFileResponse {
  success: boolean;
  userPermissions: CaseFilePermission;
}

interface CreateCareLogResponse {
  success: boolean;
  careLog?: { logId?: string; processingStatus?: string };
  error?: string;
}

export function CareLogsPage(): JSX.Element {
  const { caseFileId: urlCaseFileId } = useParams<{ caseFileId: string }>();
  const navigate = useNavigate();

  // Use CaseFile context
  const {
    caseFiles,
    activeCaseFileId,
    setActiveCaseFileId,
    refreshCaseFiles,
  } = useCaseFile();

  // Sync URL caseFileId with context
  const caseFileId = urlCaseFileId || activeCaseFileId;

  // Update context if URL has a different caseFileId
  useEffect(() => {
    if (urlCaseFileId && urlCaseFileId !== activeCaseFileId) {
      setActiveCaseFileId(urlCaseFileId);
    }
  }, [urlCaseFileId, activeCaseFileId, setActiveCaseFileId]);

  // Permission state
  const [userPermission, setUserPermission] = useState<CaseFilePermission>('viewer');
  const [caseFileError, setCaseFileError] = useState<string | null>(null);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  // Backend enqueue-failure signal on create (WORK-006 zero-recovery):
  // "failed" from createCareLog means AI processing was never enqueued and
  // will NOT self-heal — the only recovery is re-saving the text. It must
  // not be silently dropped when the form closes (Codex C-2 round-10).
  const [createWarning, setCreateWarning] = useState<string | null>(null);

  // Filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTags, setSelectedTags] = useState<CareLogTag[]>([]);

  // Fetch user permission for this CaseFile
  useEffect(() => {
    if (!caseFileId) return;

    setCaseFileError(null);

    const fetchPermission = async () => {
      try {
        const getCaseFile = httpsCallable<{ caseFileId: string }, GetCaseFileResponse>(
          functions,
          'getCaseFile'
        );
        const result = await getCaseFile({ caseFileId });
        if (result.data.success) {
          setUserPermission(result.data.userPermissions);
        }
      } catch (err) {
        console.error('Error fetching CaseFile permissions:', err);
        const firebaseError = err as { code?: string };
        const rawMessage = err instanceof Error ? err.message : '';
        if (firebaseError?.code === 'functions/resource-exhausted') {
          setCaseFileError("You're doing a lot! Please wait a moment and try again.");
        } else if (rawMessage.toLowerCase().includes('not found') || rawMessage.toLowerCase().includes('does not have access')) {
          setCaseFileError('This care record could not be found. It may have been removed or you may not have access.');
        } else {
          setCaseFileError('Something went wrong while loading this care record. Please try again.');
        }
      }
    };

    fetchPermission();
  }, [caseFileId]);

  const hasFilters = startDate || endDate || selectedTags.length > 0;

  const handleCreateLog = useCallback(async (data: { logText: string; logDate: string; tags: CareLogTag[] }) => {
    if (!caseFileId) return;

    setIsCreating(true);

    try {
      const createCareLog = httpsCallable<
        { caseFileId: string; logText: string; logDate?: string; tags?: string[] },
        CreateCareLogResponse
      >(functions, 'createCareLog');

      const result = await createCareLog({
        caseFileId,
        logText: data.logText,
        logDate: data.logDate,
        tags: data.tags,
      });

      if (result.data.success) {
        // The entry itself saved — but surface a processing enqueue failure
        // instead of discarding the returned status (the entry would
        // otherwise sit unprocessed with no visible reason to intervene).
        setCreateWarning(
          result.data.careLog?.processingStatus === 'failed' ?
            'Your entry was saved, but AI analysis could not be started. Open the entry, edit the text, and save to retry.' :
            null
        );
        setShowNewEntryForm(false);
        setRefreshKey((prev) => prev + 1);
      } else {
        throw new Error(result.data.error || 'Failed to create care log');
      }
    } catch (err) {
      console.error('Error creating care log:', err);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [caseFileId]);

  const handleLogClick = (logId: string) => {
    setSelectedLogId(logId);
  };

  const handleEditClick = (logId: string) => {
    setSelectedLogId(logId);
  };

  const handleBackFromDetail = () => {
    setSelectedLogId(null);
  };

  const handleLogUpdated = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const clearAllFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedTags([]);
  };

  const handleBackToCaseFile = () => {
    navigate(`/casefile/${caseFileId}`);
  };

  const handleCaseFileChange = (newCaseFileId: string) => {
    setActiveCaseFileId(newCaseFileId);
    navigate(`/casefile/${newCaseFileId}/carelogs`);
  };

  const handleJoinedCareTeam = (newCaseFileId: string) => {
    setActiveCaseFileId(newCaseFileId);
    refreshCaseFiles();
    navigate(`/casefile/${newCaseFileId}/carelogs`);
  };

  if (!caseFileId) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="card p-8 max-w-md text-center">
          <h2 className="text-lg font-semibold text-ink mb-2">No Care Record Selected</h2>
          <p className="text-muted mb-6">Please select a care record from the dashboard to view care logs.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (caseFileError) {
    return (
      <div className="min-h-screen bg-airy flex">
        <div className="hidden lg:block">
          <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="card p-8 max-w-md text-center">
            <h2 className="text-lg font-semibold text-ink mb-2">Care Record Not Available</h2>
            <p className="text-muted mb-6">{caseFileError}</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
      </div>

      {/* Sidebar - Mobile Overlay */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-ink/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar className="h-full" activeCaseFileId={caseFileId} />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Global Header with CaseFile Selector */}
        <Header
          caseFiles={caseFiles.map((cf) => ({ id: cf.caseFileId, name: cf.name }))}
          activeCaseFileId={caseFileId}
          onCaseFileChange={handleCaseFileChange}
          onMenuClick={() => setSidebarOpen(true)}
          showMobileMenu={true}
          onJoinedCareTeam={handleJoinedCareTeam}
        />

        {/* Page Header */}
        <div className="bg-white border-b border-soft-neutral px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToCaseFile}
                className="btn-ghost btn-sm p-1 lg:hidden"
                aria-label="Back to CaseFile"
              >
                <ArrowLeft size={20} aria-hidden="true" />
              </button>
              <button
                onClick={handleBackToCaseFile}
                className="hidden lg:flex btn-ghost btn-sm items-center gap-1"
              >
                <ArrowLeft size={16} />
                Back to CaseFile
              </button>
              <div className="h-6 w-px bg-soft-neutral hidden lg:block" />
              <h1 className="text-xl font-semibold text-ink">Care Logs</h1>
            </div>
            {hasWriteAccess(userPermission) && (
              <button
                onClick={() => setShowNewEntryForm(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">New Entry</span>
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div id="main-content" role="main" tabIndex={-1} className="flex-1 p-4 lg:p-6 outline-none">
          <div className="max-w-3xl mx-auto">
            {/* Enqueue-failure warning from the last create */}
            {createWarning && (
              <div
                role="alert"
                className="mb-6 flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800"
              >
                <AlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                <p className="flex-1 text-sm">{createWarning}</p>
                <button
                  onClick={() => setCreateWarning(null)}
                  className="btn-ghost btn-sm p-1"
                  aria-label="Dismiss warning"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {/* New Entry Form */}
            {showNewEntryForm && (
              <div className="mb-6">
                <div className="card">
                  <div className="card-header flex justify-between items-center">
                    <h3 className="font-semibold text-ink">New Care Log Entry</h3>
                    <button
                      onClick={() => setShowNewEntryForm(false)}
                      className="btn-ghost btn-sm p-1"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="card-body">
                    <CareLogForm
                      onSubmit={handleCreateLog}
                      onCancel={() => setShowNewEntryForm(false)}
                      submitLabel="Save Entry"
                      isLoading={isCreating}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Detail View (when a log is selected) */}
            {selectedLogId && (
              <div className="mb-6">
                <CareLogDetail
                  caseFileId={caseFileId}
                  logId={selectedLogId}
                  onBack={handleBackFromDetail}
                  onUpdate={handleLogUpdated}
                />
              </div>
            )}

            {/* List View (when no log is selected) */}
            {!selectedLogId && (
              <>
                {/* Filters Toggle */}
                <div className="mb-4 flex items-center justify-between">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`btn-ghost btn-sm flex items-center gap-2 ${hasFilters ? 'text-evergreen' : ''}`}
                  >
                    <Filter size={16} />
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                    {hasFilters && (
                      <span className="badge badge-primary badge-sm">{selectedTags.length + (startDate || endDate ? 1 : 0)}</span>
                    )}
                  </button>
                  {hasFilters && (
                    <button
                      onClick={clearAllFilters}
                      className="btn-ghost btn-sm text-muted hover:text-ink"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>

                {/* Filters Panel */}
                {showFilters && (
                  <div className="card mb-6">
                    <div className="card-body space-y-4">
                      <DateRangeFilter
                        startDate={startDate}
                        endDate={endDate}
                        onStartDateChange={setStartDate}
                        onEndDateChange={setEndDate}
                        onClear={() => { setStartDate(''); setEndDate(''); }}
                      />
                      <TagFilter
                        selectedTags={selectedTags}
                        onTagsChange={setSelectedTags}
                        onClear={() => setSelectedTags([])}
                      />
                    </div>
                  </div>
                )}

                {/* Care Logs List */}
                <CareLogsList
                  caseFileId={caseFileId}
                  startDate={startDate || undefined}
                  endDate={endDate || undefined}
                  tags={selectedTags.length > 0 ? selectedTags : undefined}
                  onLogClick={handleLogClick}
                  onEditClick={handleEditClick}
                  refreshKey={refreshKey}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CareLogsPage;
